import { Application, DisplayObject, Container } from '@tencent/alloy-renderer';
import { BrowserTarget } from '@tencent/alloy-renderer-browser';
import { IRenderArea, IRegionRenderProps, IRenderConfig } from './player-layer-interface';

export default class PlayerRenderArea implements IRenderArea {
    private application: Application | null;
    private scale: number;
    private width: number;
    private height: number;
    private renderComponent: Container | null;

    public constructor(props: IRegionRenderProps) {
        const { scale, width, height, canvasWrapper, autoRender } = props;
        this.scale = scale;
        this.width = width * scale;
        this.height = height * scale;

        const target = new BrowserTarget();
        const canvasElement = document.createElement('canvas');
        canvasElement.style.pointerEvents = 'none';
        canvasWrapper.appendChild(canvasElement);
        this.application = new Application(target, {
            native: canvasElement,
            interactive: false,
            autoRender,
        });
        this.application.resolution = Math.ceil(window.devicePixelRatio);
        this.buildSlideComponent();
        this.updateInfo(this.width, this.height, scale);
    }

    /**
     * 获取application
     * @returns Application
     */
    public getApplication(): Application | null {
        return this.application;
    }

    /**
     * 从头部添加操作元素
     */
    public prependChild(component: DisplayObject): void {
        this.renderComponent?.addChildAt(component, 0);
    }

    /**
     * 从尾部添加操作元素
     */
    public appendChild(component: DisplayObject): void {
        this.renderComponent?.addChild(component);
    }

    /**
     * 移除元素
     */
    public removeChild(component: DisplayObject): void {
        this.renderComponent?.removeChild(component);
    }

    /**
     * 销毁application
     */
    public disposeApplication(): void {
        // alloy-render中dispose只是移除了事件监听、清空了cache、以及清空画布，并没有移除child
        this.application?.dispose();
        while (this.application?.stage.children[0]) {
            this.application?.stage.removeChild(this.application?.stage.children[0]);
        }
        this.renderComponent?.dispose();
        this.clearRenderComponent();
        if (this.application?.view) {
            this.application.view.width = 0;
            this.application.view.height = 0;
        }
        this.application = null;
        this.renderComponent = null;
    }

    /**
     * 清理全部元素
     * 操作层是复用当前元素层component，所以并不是真的的清理
     * 因为可能还要挂回当前元素层，不用dispose
     */
    public clearRenderComponent(): void {
        while (this.renderComponent?.children[0]) {
            this.renderComponent?.removeChild(this.renderComponent?.children[0]);
        }
    }

    /**
     * 更新宽、高、缩放比例
     * @param config
     */
    public updateRenderRect(config: IRenderConfig): void {
        const { width, height, scale } = config;
        if (
            this.width === width
            && this.height === height
            && this.scale === scale
        ) return;
        this.width = width;
        this.height = height;
        this.scale = scale;
        this.updateInfo(width, height, scale);
    }

    /**
     * 找到动画目标元素
     * @param targetModelKey 动画目标元素的key
     * @returns DisplayObject
     */
    public findAnimTarget(targetModelKey: string): DisplayObject | undefined {
        return this.renderComponent?.children.find((childComponent: any) => {
            const modelKey = childComponent?.factoryOptions?.model?.key;
            return modelKey === targetModelKey;
        });
    }

    /**
     * 创建Slide区域
     */
    private buildSlideComponent() {
        this.renderComponent = new Container();
        this.renderComponent.x = 0;
        this.renderComponent.y = 0;
        this.application?.stage.addChild(this.renderComponent);
    }

    /**
     * 更新需要的一些信息
     * @param width
     * @param height
     * @param scale
     */
    private updateInfo(width: number, height: number, scale: number): void {
        if (!this.application) return;
        const roundWidth = Math.round(width);
        const roundHeight = Math.round(height);
        this.application.width = roundWidth;
        this.application.height = roundHeight;
        this.application.stage.transform.scaleX = scale;
        this.application.stage.transform.scaleY = scale;
        if (this.renderComponent) {
            this.renderComponent.x = 0;
            this.renderComponent.y = 0;
        }
        this.application.requestRender();
    }
}
